<script setup lang="ts">
import { useI18n } from '#imports';
import type { NuxtError } from '#app';

const props = defineProps({
  error: Object as () => NuxtError,
});

const { t } = useI18n();
// Handle error reset
const handleError = () => {
  clearError({ redirect: '/' });
};
</script>

<template>
  <div class="error-container">
    <div class="blurHero" />
    <div class="blurHero secondary" />

    <section class="h-screen w-full">
      <div
        class="relative m-auto flex min-h-screen max-w-3xl flex-col items-center justify-center gap-[5vh]"
      >
        <!-- Illustration -->
        <div class="illustration-container animate-fade-slide">
          <div class="text-4xl">{{ error?.statusCode || '500' }}</div>
        </div>

        <!-- Content -->
        <h1
          class="gradientHero animate-fade-slide animation-delay-200 text-center text-5xl font-bold md:text-7xl md:leading-20"
        >
          {{
            error?.statusCode === 404
              ? t('error.notFound')
              : error?.statusCode === 500
                ? t('error.serverError')
                : error?.statusCode === 503
                  ? t('error.maintenance')
                  : t('error.oops')
          }}
        </h1>

        <p
          class="animate-fade-slide animation-delay-400 mx-auto max-w-2xl text-center text-xl leading-8 font-normal md:text-2xl"
        >
          {{ error?.statusMessage || t('error.description') }}
        </p>
        <div class="buttons-container">
          <PrimeButton
            as="router-link"
            to="/"
            :label="t('error.backToHome')"
            size="large"
            class="heroButton primary-button"
          />
          <PrimeButton
            as="a"
            href="mailto:contact@cobe1.com"
            :label="t('error.contactSupport')"
            severity="secondary"
            size="large"
            class="secondary-button"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.error-container {
  position: relative;
  overflow: hidden;
  background: var(--surface-ground);
}

.blurHero {
  position: absolute;
  top: 30%;
  left: 20%;
  width: 40vw;
  height: 40vw;
  border-radius: 50%;
  filter: blur(150px);
  opacity: 0.3;
  background: var(--p-primary-500);
  animation: float 8s ease-in-out infinite;
}

.blurHero.secondary {
  top: 50%;
  left: 60%;
  width: 30vw;
  height: 30vw;
  background: var(--p-primary-700);
  animation-delay: -4s;
}

.error-code {
  font-size: 180px;
  font-weight: 900;
  line-height: 1;
  background: linear-gradient(
    135deg,
    var(--p-primary-500),
    var(--p-primary-700)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0.1;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.gradientHero {
  background: linear-gradient(
    135deg,
    var(--p-primary-500),
    var(--p-primary-700)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.buttons-container {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

@media (max-width: 768px) {
  .error-code {
    font-size: 120px;
  }

  .buttons-container {
    flex-direction: column;
    width: 100%;
    padding: 0 1rem;
  }

  .illustration-container {
    width: 250px;
    height: 150px;
  }
}
</style>
